import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router';
import { AnimatePresence } from 'framer-motion';
import { submitQuizAnswers, submitQuestionAnswer } from '../../services/quizService';
import QuizCompletionModal from '../../components/modals/QuizCompletionModal';
import ConfirmSubmitModal from '../../components/modals/ConfirmSubmitModal';
import BackLink from '../../components/ui/BackLink';
import IdentityVerification from './components/verification/IdentityVerification';
import QuestionCard from './components/QuestionCard';
import { toast } from 'sonner';
import { formatTimeLeft } from './utils/utils';
import { clearSession } from './utils/quizSessionStorage';
import QuizQuestionTracker from './components/QuizQuestionTracker';
import { Helmet } from "react-helmet-async";
import useQuizTimer from '../../hooks/quiz/useQuizTimer';
import useQuizAnswers from '../../hooks/quiz/useQuizAnswers';
import LiveProctoringMonitor from './components/proctoring/LiveProctoringMonitor';
import PreQuizSetupFlow from './components/verification/PreQuizSetupFlow';
import useQuizSetup from '../../hooks/quiz/useQuizSetup';
import ChallengeQuestionModal from './components/verification/ChallengeQuestionModal';
import useChallengeQuestions from '../../hooks/quiz/useChallengeQuestions';
import useTabSwitchDetection from '../../hooks/quiz/useTabSwitchDetection';
import useExamFullscreen from '../../hooks/quiz/useExamFullscreen';
import useSessionTelemetry from '../../hooks/quiz/useSessionTelemetry';
import QuizCalculator from './components/QuizCalculator';
import { Calculator } from 'lucide-react';

const TakeQuizPage = () => {
  const { quiz_id } = useParams();
  const navigate = useNavigate();
  const handleModalClose = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const {
    quiz,
    session,
    questions,
    loading,
    error,
    verificationState,
    setVerificationState,
    showSetupFlow,
    calibratedBaselines,
    sessionInfo,
    handleSetupComplete,
    initializeQuiz
  } = useQuizSetup(quiz_id, handleModalClose);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [checkAnswers, setCheckAnswers] = useState({}); // { [questionId]: true/false } used to check if a question has been checked or not

  const [direction, setDirection] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showSubmitConfirmModal, setShowSubmitConfirmModal] = useState(false);
  const [submittedQuestions, setSubmittedQuestions] = useState(new Set());
  const [timeLeft, setTimeLeft] = useState(null);
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);

  const isCalculatorEnabled = Boolean(
    quiz?.enable_calculator ?? session?.enable_calculator ?? sessionInfo?.enable_calculator
  );

  useEffect(() => {
    if (!isCalculatorEnabled && isCalculatorOpen) {
      setIsCalculatorOpen(false);
    }
  }, [isCalculatorEnabled, isCalculatorOpen]);

  // track answers in a ref to access latest state in timer
  const answersRef = useRef(answers);
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  // track submitted questions in a ref for auto-submit handlers
  const submittedRef = useRef(submittedQuestions);
  useEffect(() => {
    submittedRef.current = submittedQuestions;
  }, [submittedQuestions]);

  // Persist answers to localStorage and restore on reload
  useQuizAnswers(quiz_id, sessionInfo?.sessionId, answers, setAnswers, !loading && !!quiz);

  // Orchestrate dynamic challenge verification questions
  const {
    activeChallenge,
    saveAnswerAndNavigate,
    handleResolveChallenge,
    handleChallengeTimeout,
  } = useChallengeQuestions({
    sessionId: sessionInfo?.sessionId,
    currentQuestionIndex,
    questions,
    answers,
    setDirection,
    setCurrentQuestionIndex,
    setSubmittedQuestions,
    setIsSubmitting,
  });

  // Submit the current question's answer individually (save-as-you-go)
  const submitCurrentAnswer = useCallback(async () => {
    const sessionId = sessionInfo?.sessionId;
    if (!sessionId || currentQuestionIndex < 0) return;

    const currentQuestion = questions[currentQuestionIndex];
    if (!currentQuestion) return;

    const questionId = currentQuestion.id;
    if (submittedRef.current.has(questionId)) return; // already submitted

    const answer = answersRef.current[questionId];
    if (!answer) return; // no answer yet

    try {
      await submitQuestionAnswer(sessionId, answer);
      setSubmittedQuestions(prev => new Set([...prev, questionId]));
    } catch (err) {
      console.error('Failed to submit individual answer:', err);
      // Don't block navigation — we still have the answer in localStorage
    }
  }, [sessionInfo?.sessionId, currentQuestionIndex, questions]);

  // Capture activity and keystroke telemetry for exam quizzes
  const { flushTelemetry } = useSessionTelemetry({
    enabled: quiz?.quiz_type === "exam" && !verificationState && !showSetupFlow,
    sessionId: sessionInfo?.sessionId,
    currentQuestionId: questions[currentQuestionIndex]?.id,
  });

  // Define handleSubmitQuiz early (with useCallback) so it can be used by the timer and tab switch hooks
  const handleSubmitQuiz = useCallback(async () => {
    if (!sessionInfo?.sessionId) return;

    let currentAnswers = answersRef.current;

    try {
      setIsSubmitting(true);

      // Submit current question answer if not already submitted
      const currentQ = questions[currentQuestionIndex];
      if (currentQ) {
        const qId = currentQ.id;
        const answer = currentAnswers[qId];
        if (answer && !submittedRef.current.has(qId)) {
          await submitQuestionAnswer(sessionInfo?.sessionId, answer);
        }
      }

      await submitQuizAnswers(sessionInfo?.sessionId, Object.values(currentAnswers));
      
      // Flush any remaining activity and keystroke telemetry before ending the session
      try {
        await flushTelemetry();
      } catch (telemetryErr) {
        console.warn('Failed to flush final telemetry:', telemetryErr);
      }

      clearSession(quiz_id, sessionInfo?.sessionId);
      setShowCompletionModal(true); // Show completion modal instead of navigating
    } catch (error) {
      console.error('Error submitting quiz:', error);
      toast.error('Failed to submit quiz');
    } finally {
      setIsSubmitting(false);
    }
  }, [sessionInfo?.sessionId, quiz_id, questions, currentQuestionIndex, flushTelemetry]);

  // Componentized timer hook
  useQuizTimer({
    expectedDuration: quiz?.expected_duration,
    startedAt: (verificationState || showSetupFlow) ? null : sessionInfo?.startedAt,
    onTimeUp: handleSubmitQuiz,
    quizType: quiz?.quiz_type,
    setTimeLeft,
  });

  // Track tab switches for exam quizzes — auto-submit after 3 switches
  useTabSwitchDetection({
    enabled: quiz?.quiz_type === "exam" && !verificationState && !showSetupFlow,
    quizId: quiz_id,
    onThresholdReached: handleSubmitQuiz,
  });

  // Enforce fullscreen mode for exam quizzes — auto-submit after 3 exits
  useExamFullscreen({
    enabled: quiz?.quiz_type === "exam" && !verificationState && !showSetupFlow,
    loaded: !loading && !verificationState && !showSetupFlow && !!quiz,
    onThresholdReached: () => { },
    threshold: 3,
  });

  const isAnyQuestionUnanswered = () => {
    return questions.some((question) => {
      const answer = answers[question.id];

      if (question.question_type === 'objective') {
        return !answer?.selected_option;
      } else if (question.question_type === 'theory') {
        return !answer?.theory_answer?.trim();
      }
    });
  };

  const getAnsweredCount = () => {
    return questions.filter((question) => {
      const answer = answers[question.id];
      if (!answer) return false;
      if (question.question_type === 'objective') return !!answer.selected_option;
      if (question.question_type === 'multiple_select') return answer.selected_options?.length > 0;
      if (question.question_type === 'theory') return !!answer.theory_answer?.trim();
      if (question.question_type === 'matching') return Array.isArray(answer.matching_answers) && answer.matching_answers.length === question.matching_pair?.length;
      return false;
    }).length;
  };

  const handleNext = async () => {
    if (currentQuestionIndex === questions.length - 1) {
      if (isAnyQuestionUnanswered()) {
        toast.error('Please answer all questions before submitting.');
        return;
      }
      setShowSubmitConfirmModal(true);
    } else {
      if (quiz?.challenge_questions_enabled) {
        await saveAnswerAndNavigate(currentQuestionIndex + 1, 1);
      } else {
        setDirection(1);
        await submitCurrentAnswer();
        setCurrentQuestionIndex(prev => prev + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setDirection(-1);
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleMultipleSelect = (optionId) => {
    setAnswers(prev => {
      const prevSelected = prev[currentQuestion.id]?.selected_options || [];
      const alreadySelected = prevSelected.includes(optionId);
      const newSelected = alreadySelected
        ? prevSelected.filter(id => id !== optionId)
        : [...prevSelected, optionId];
      return {
        ...prev,
        [currentQuestion.id]: {
          question: currentQuestion.id,
          type: 'multiple_select',
          selected_options: newSelected,
        }
      };
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="loading loading-spinner text-primary w-12 h-12"></span>
      </div>
    );
  }

  if (showSetupFlow) {
    return (
      <>
        <Helmet>
          <title>Quiz Setup & Rules | ExamsPrep</title>
        </Helmet>
        <div className="max-w-3xl mx-auto p-4 animate-fade-in">
          <PreQuizSetupFlow
            quiz={quiz}
            onComplete={handleSetupComplete}
            onCancel={() => navigate(-1)}
          />
        </div>
      </>
    );
  }

  if (verificationState) {
    return (
      <>
        <Helmet>
          <title>Verification Required | ExamsPrep</title>
        </Helmet>
        <div className="max-w-3xl mx-auto p-4 animate-fade-in">
          <IdentityVerification
            quizId={quiz_id}
            quizTitle={quiz?.topic_detail?.title || quiz?.course_detail?.title || quiz?.title || 'Quiz'}
            initialStep={verificationState}
            onSuccess={() => {
              setVerificationState(null);
              initializeQuiz();
            }}
            onCancel={() => navigate(-1)}
          />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        {/* <h1 className="text-xl font-semibold mb-2">Something went wrong</h1> */}
        <p className="mb-4 text-lg">{error}</p>
        <button
          className="btn btn-outline"
          onClick={() => navigate(-1)}
        >
          Go Back
        </button>
      </div>
    );
  }

  if (!quiz) {
    return <div className="text-center py-4">Quiz data not available.</div>;
  }

  if (!questions || questions.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="mb-4 text-lg">No questions are currently available for this quiz.</p>
        <button className="btn btn-outline" onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }

  const handleViewResults = () => {
    // Navigate to results and replace the current quiz page
    navigate(`/quizzes/${sessionInfo.sessionId}/results`, { replace: true });
  };

  const currentQuestion = questions[currentQuestionIndex];

  let pageTitle = "Take Quiz";
  if (quiz?.title) {
    pageTitle = `Take ${quiz.title}`;
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle} | ExamPrep</title>
      </Helmet>

      <div className="max-w-3xl mx-auto p-4">

        <div className="flex justify-between items-center flex-wrap gap-4 mb-4">
          <BackLink />
          <div className="flex flex-wrap justify-end sm:justify-normal items-center gap-4">
            <div className="badge badge-lg badge-primary badge-outline p-3 h-fit">{quiz?.topic_detail?.title || quiz?.course_title}</div>
            {timeLeft !== null && (
              <div className={`badge badge-lg  ${timeLeft < 60 ? 'badge-error' : 'badge-outline'} p-3`}>
                Time Left: {formatTimeLeft(timeLeft)}
              </div>
            )}
          </div>
        </div>

        <QuizQuestionTracker
          quiz_type={quiz.quiz_type}
          questions={questions}
          answers={answers}
          currentIndex={currentQuestionIndex}
          onJump={(idx) => {
            if (quiz?.challenge_questions_enabled) {
              saveAnswerAndNavigate(idx, idx > currentQuestionIndex ? 1 : -1);
            } else {
              setCurrentQuestionIndex(idx);
              setDirection(idx > currentQuestionIndex ? 1 : -1);
            }
          }}
          checkAnswers={checkAnswers}
        />

        {/* Quiz Progress */}
        <div className="card bg-base-200 shadow-md mb-8">
          <div className="card-body p-4">
            <div className="flex justify-between items-center">
              <h2 className="card-title text-lg">Quiz Progress</h2>
              <div className="text-sm">Question {currentQuestionIndex + 1} of {questions.length}</div>
            </div>
            <progress className="progress progress-primary w-full" value={currentQuestionIndex + 1} max={questions.length}></progress>
          </div>
        </div>

        {questions.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-5xl mb-4">😢</div>
            <h1 className="text-xl text-gray-600">No questions have been added to this quiz yet </h1>
            <button
              className="btn btn-outline mt-4"
              onClick={() => navigate(-1)}
            >
              Go Back
            </button>
          </div>
        ) : (
          <>
            <div className="relative mb-4 overflow-x-hidden">
              <AnimatePresence initial={false} custom={direction}>
                <QuestionCard
                  key={currentQuestion.id}
                  quizType={quiz.quiz_type}
                  question={currentQuestion}
                  answer={answers[currentQuestion.id] || {}}
                  questionNumber={currentQuestionIndex + 1}
                  onOptionSelect={(optionId) => {
                    setAnswers(prev => ({
                      ...prev,
                      [currentQuestion.id]: {
                        question: currentQuestion.id,
                        type: 'objective',
                        selected_option: optionId,
                      }
                    }));
                  }}
                  theoryAnswer={answers[currentQuestion.id]?.theory_answer || ''}
                  onTheoryChange={(value) => setAnswers(prev => ({
                    ...prev,
                    [currentQuestion.id]: {
                      question: currentQuestion.id,
                      type: 'theory',
                      theory_answer: value
                    }
                  }))}
                  onMultipleSelect={handleMultipleSelect}
                  onMatchingChange={(pairId, selectedRight) => {
                    setAnswers(prev => {
                      const prevMatches = prev[currentQuestion.id]?.matching_answers || [];
                      const updatedMatches = prevMatches.find(match => match.matching_pair == pairId) ?
                        prevMatches.map((match) => (match.matching_pair == pairId ? { ...match, selected_right: selectedRight } : match)) :
                        [...prevMatches, { matching_pair: pairId, selected_right: selectedRight }]

                      return {
                        ...prev,
                        [currentQuestion.id]: {
                          question: currentQuestion.id,
                          type: 'matching',
                          matching_answers: updatedMatches
                        }
                      }
                    })
                  }}
                  direction={direction}
                  showFeedback={checkAnswers[currentQuestion.id] || false}
                  checkAnswer={checkAnswers[currentQuestion.id] || false}
                  onCheckAnswer={(questionId) => {
                    setCheckAnswers(prev => ({ ...prev, [questionId]: true }));
                  }}
                  onUncheckAnswer={(questionId) => {
                    setCheckAnswers(prev => ({ ...prev, [questionId]: false }));
                  }}
                />
              </AnimatePresence>
            </div>

            <div className="flex justify-between mb-8">
              <button
                className="btn btn-outline"
                onClick={handlePrevious}
                disabled={currentQuestionIndex === 0 || isSubmitting}
              >
                Previous
              </button>
              <button
                className="btn btn-primary"
                onClick={handleNext}
                disabled={isSubmitting}
              >
                {currentQuestionIndex === questions.length - 1 ? (
                  isSubmitting ? 'Submitting...' : 'Submit'
                ) : 'Next'}
              </button>
            </div>
          </>
        )}

        <ConfirmSubmitModal
          isOpen={showSubmitConfirmModal}
          onConfirm={() => {
            setShowSubmitConfirmModal(false);
            handleSubmitQuiz();
          }}
          onCancel={() => setShowSubmitConfirmModal(false)}
          isSubmitting={isSubmitting}
          totalQuestions={questions.length}
          answeredCount={getAnsweredCount()}
        />
        <QuizCompletionModal
          isOpen={showCompletionModal}
          sessionId={sessionInfo?.sessionId}
          quizId={quiz_id}
          topicId={quiz?.topic_id || quiz?.topic_detail?.id || quiz?.topic}
        />
        <ChallengeQuestionModal
          isOpen={!!activeChallenge}
          challengeEvent={activeChallenge}
          onSubmitAnswer={handleResolveChallenge}
          onTimeout={handleChallengeTimeout}
        />

        {/* Right-edge docked Calculator tab & Floating Exam Calculator */}
        {isCalculatorEnabled && (
          <>
            <button
              type="button"
              onClick={() => setIsCalculatorOpen((prev) => !prev)}
              className={`fixed right-0 top-1/2 -translate-y-1/2 z-40 flex flex-col items-center justify-center gap-1.5 px-2 py-3.5 rounded-l-xl shadow-2xl transition-all duration-200 cursor-pointer group ${
                isCalculatorOpen
                  ? 'bg-base-300 text-primary border-l border-t border-b border-primary/40'
                  : 'bg-primary text-primary-content hover:pr-3 hover:shadow-primary/30'
              }`}
              title={isCalculatorOpen ? 'Close Calculator' : 'Open Calculator'}
              aria-label="Toggle Calculator"
            >
              <Calculator className={`w-5 h-5 transition-transform ${isCalculatorOpen ? 'scale-110' : 'group-hover:scale-110'}`} />
              <span className="text-[10px] font-bold tracking-wider uppercase [writing-mode:vertical-rl] rotate-180">
                {isCalculatorOpen ? 'Close' : 'Calc'}
              </span>
            </button>

            <QuizCalculator
              isOpen={isCalculatorOpen}
              onClose={() => setIsCalculatorOpen(false)}
            />
          </>
        )}
      </div>

      {/* Live Proctoring Monitor */}
      {quiz?.proctoring_enabled && (
        <LiveProctoringMonitor
          sessionId={sessionInfo?.sessionId}
          isActive={!loading && !verificationState && !showSetupFlow && !!quiz}
          preCalibratedBaselines={calibratedBaselines}
        />
      )}
    </>
  );
};

export default TakeQuizPage;